import React, { useState, useRef, lazy } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Lock, Gift, MapPin, ChevronLeft, Bell, BellOff, CheckCircle, Flame, ArrowLeft, ShoppingBag, Image as ImageIcon, Mic, Flag, Target, Music, Award, DollarSign, Send, ChevronRight, Video, Sparkles, X } from 'lucide-react';
import { VideoGenerationUpsell } from './VideoGenerationUpsell';
interface ZoeyAngelFeedProps {
  onBack: () => void;
}
export const ZoeyAngelFeed: React.FC<ZoeyAngelFeedProps> = ({
  onBack
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [isFollowing, setIsFollowing] = useState(false);
  const [likedPosts, setLikedPosts] = useState<Record<string, boolean>>({});
  const [showNotificationOptions, setShowNotificationOptions] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [showPollResults, setShowPollResults] = useState(false);
  const [pollVote, setPollVote] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockPostId, setUnlockPostId] = useState<string | null>(null);
  const [showImageGeneratorModal, setShowImageGeneratorModal] = useState(false);
  const [showVideoUpsellModal, setShowVideoUpsellModal] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabs = [{
    id: 'all',
    label: 'All',
    icon: <Flame size={14} data-id="element-23055" />
  }, {
    id: 'media',
    label: 'Media',
    icon: <ImageIcon size={14} data-id="element-23056" />
  }, {
    id: 'voice',
    label: 'Voice',
    icon: <Mic size={14} data-id="element-23057" />
  }, {
    id: 'missions',
    label: 'Missions',
    icon: <Target size={14} data-id="element-23058" />
  }, {
    id: 'moans',
    label: 'Moans',
    icon: <Music size={14} data-id="element-23059" />
  }, {
    id: 'polls',
    label: 'Polls',
    icon: <Flag size={14} data-id="element-23060" />
  }, {
    id: 'upsells',
    label: 'Upsells',
    icon: <Lock size={14} data-id="element-23061" />
  }, {
    id: 'rewards',
    label: 'Rewards',
    icon: <Award size={14} data-id="element-23062" />
  }, {
    id: 'shop',
    label: 'Shop',
    icon: <ShoppingBag size={14} data-id="element-23063" />
  }];
  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabsRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      tabsRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const toggleFollow = () => {
    setIsFollowing(!isFollowing);
  };
  const toggleNotification = () => {
    setNotificationEnabled(!notificationEnabled);
    setShowNotificationOptions(false);
  };
  const toggleLike = (postId: string) => {
    setLikedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };
  const handlePollVote = (option: string) => {
    setPollVote(option);
    setShowPollResults(true);
  };
  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would submit the comment
    setCommentText('');
  };
  const handleUnlock = (postId: string) => {
    setUnlockPostId(postId);
    setShowUnlockModal(true);
  };
  const confirmUnlock = () => {
    // Here you would handle the actual token transaction
    setShowUnlockModal(false);
  };
  const posts = [{
    id: 'post1',
    type: 'premium_content',
    content: "Just got back from my bartending shift... want to see what I wear underneath my uniform? 💙 I promise it's worth the unlock...",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fnQWPJWMus62iBnJCYdU4W/z1.png',
    likes: 2452,
    comments: 187,
    time: '1h ago',
    locked: true,
    price: 5,
    location: 'After Work',
    hashtags: ['#bartender', '#secretunderneath', '#exclusive', '#blonde']
  }, {
    id: 'post2',
    type: 'poll',
    content: "What should I wear for tonight's shift? Help me choose and I'll send a special pic to whoever votes! 💋",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fnQWPJWMus62iBnJCYdU4W/z2.png',
    likes: 1789,
    comments: 312,
    time: '3h ago',
    location: 'Getting Ready',
    poll: {
      options: [{
        text: 'Short black dress 👗',
        percentage: 65
      }, {
        text: 'Tight jeans and crop top 👖',
        percentage: 35
      }],
      endsIn: '1 hour'
    },
    hashtags: ['#outfitpoll', '#bartenderlife', '#helpme', '#fashion']
  }, {
    id: 'post3',
    type: 'voice_note',
    content: 'Made this voice note just for my special fans... listen to what I whisper when no one else is around 🎧',
    likes: 3231,
    comments: 412,
    time: '2d ago',
    location: 'Late Night',
    audioWaveform: [0.2, 0.5, 0.3, 0.8, 0.4, 0.6, 0.3, 0.5, 0.7, 0.8, 0.5, 0.3, 0.6, 0.4, 0.2],
    duration: '0:36',
    hashtags: ['#voice', '#whisper', '#asmr', '#intimate']
  }];
  // Filter posts based on active tab
  const filteredPosts = posts.filter(post => {
    if (activeTab === 'all') return true;
    if (activeTab === 'media' && (post.type === 'image' || post.type === 'premium_content')) return true;
    if (activeTab === 'voice' && post.type === 'voice_note') return true;
    if (activeTab === 'polls' && post.type === 'poll') return true;
    if (activeTab === 'upsells' && post.type === 'premium_content') return true;
    return false;
  });
  return <section className="w-full max-w-[860px] mx-auto space-y-4 angel-feed-container" data-id="element-23064">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-950/90 backdrop-blur-sm p-3 flex items-center justify-between border-b border-gray-800" data-id="element-23065">
        <div className="flex items-center gap-4" data-id="element-23066">
          <button className="p-2 rounded-full hover:bg-gray-800" onClick={onBack} data-id="element-23067">
            <ArrowLeft size={20} data-id="element-23068" />
          </button>
          <div data-id="element-23069">
            <h1 className="font-bold text-lg" data-id="element-23070">Zoey</h1>
            <p className="text-xs text-gray-400" data-id="element-23071">1.8M Followers</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-23072">
          <button className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 rounded-full text-white text-xs font-medium" onClick={() => setShowImageGeneratorModal(true)} data-id="element-23073">
            <ImageIcon size={12} className="text-blue-400" data-id="element-23074" />
            <span data-id="element-23075">Generate Image</span>
          </button>
          <button className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 rounded-full text-white text-xs font-medium" onClick={() => setShowVideoUpsellModal(true)} data-id="element-23076">
            <Video size={12} className="text-blue-400" data-id="element-23077" />
            <span data-id="element-23078">Generate Video</span>
          </button>
          <div className="relative" data-id="element-23079">
            <button className="p-2 bg-gray-800 rounded-full hover:bg-gray-700" onClick={() => setShowNotificationOptions(!showNotificationOptions)} data-id="element-23080">
              {notificationEnabled ? <Bell size={20} data-id="element-23081" /> : <BellOff size={20} data-id="element-23082" />}
            </button>
            {showNotificationOptions && <div className="absolute right-0 mt-2 w-48 bg-gray-900 rounded-lg shadow-lg border border-gray-800 z-10" data-id="element-23083">
                <button className="w-full px-4 py-2 text-left hover:bg-gray-800 rounded-t-lg flex items-center gap-2" onClick={toggleNotification} data-id="element-23084">
                  {notificationEnabled ? <>
                      <BellOff size={16} data-id="element-23085" />
                      <span data-id="element-23086">Disable notifications</span>
                    </> : <>
                      <Bell size={16} data-id="element-23087" />
                      <span data-id="element-23088">Enable notifications</span>
                    </>}
                </button>
              </div>}
          </div>
          <button className={`px-4 py-1.5 rounded-full font-bold ${isFollowing ? 'bg-gray-800 text-white hover:bg-red-600 hover:text-white' : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:opacity-90'}`} onClick={toggleFollow} data-id="element-23089">
            {isFollowing ? 'Following' : 'Follow'}
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 transition-colors" data-id="element-23090">
            <Sparkles size={16} className="text-blue-400" data-id="element-23091" />
          </button>
        </div>
      </div>

      {/* Profile Banner */}
      <div className="relative" data-id="element-23092">
        <div className="h-32 w-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-xl" data-id="element-23093"></div>
        <div className="absolute -bottom-16 left-4 w-32 h-32 rounded-full border-4 border-gray-950 overflow-hidden" data-id="element-23094">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fnQWPJWMus62iBnJCYdU4W/z1.png" alt="Zoey" className="w-full h-full object-cover image-high-quality" loading="lazy" sizes="128px" data-id="element-23095" />
        </div>
      </div>

      {/* Profile Info */}
      <div className="mt-20 px-4 ml-36" data-id="element-23096">
        <div className="flex items-center gap-1" data-id="element-23097">
          <h2 className="text-xl font-bold" data-id="element-23098">Zoey</h2>
          <CheckCircle size={16} className="text-blue-400" fill="currentColor" data-id="element-23099" />
          <span className="text-sm font-normal text-blue-400 ml-2" data-id="element-23100">💙💋</span>
        </div>
        <p className="text-gray-400" data-id="element-23101">@ZoeyBlonde</p>
        <p className="mt-2" data-id="element-23102">
          Bartender by night, your fantasy by day. Blue eyes that'll make you
          want to order another round... 😉
        </p>
        <div className="flex gap-4 mt-3 text-sm" data-id="element-23103">
          <div className="flex items-center gap-1" data-id="element-23104">
            <span className="font-bold" data-id="element-23105">218</span>
            <span className="text-gray-400" data-id="element-23106">Following</span>
          </div>
          <div className="flex items-center gap-1" data-id="element-23107">
            <span className="font-bold" data-id="element-23108">1.8M</span>
            <span className="text-gray-400" data-id="element-23109">Followers</span>
          </div>
          <div className="flex items-center gap-1" data-id="element-23110">
            <span className="font-bold" data-id="element-23111">420</span>
            <span className="text-gray-400" data-id="element-23112">Intimacy XP</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mt-4 relative border-b border-gray-800" data-id="element-23113">
        <button className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-gray-900/80 rounded-full text-gray-400 hover:text-white" onClick={() => scrollTabs('left')} data-id="element-23114">
          <ChevronLeft size={18} data-id="element-23115" />
        </button>
        <div ref={tabsRef} className="flex overflow-x-auto scrollbar-hide px-8 py-1" data-id="element-23116">
          {tabs.map(tab => <button key={tab.id} className={`px-4 py-2 flex items-center gap-1.5 whitespace-nowrap rounded-full mx-1 ${activeTab === tab.id ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800'}`} onClick={() => setActiveTab(tab.id)} data-id="element-23117">
              {tab.icon}
              <span data-id="element-23118">{tab.label}</span>
            </button>)}
        </div>
        <button className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-gray-900/80 rounded-full text-gray-400 hover:text-white" onClick={() => scrollTabs('right')} data-id="element-23119">
          <ChevronRight size={18} data-id="element-23120" />
        </button>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4" data-id="element-23121">
        {filteredPosts.length > 0 ? filteredPosts.map(post => <div key={post.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden animate-in fade-in duration-300 angel-feed-post shadow-md hover:shadow-xl hover:shadow-blue-600/5 transition-all duration-300" data-id="element-23122">
              {/* Post Header */}
              <div className="p-3 flex items-center gap-2 border-b border-gray-800" data-id="element-23123">
                <div className="w-10 h-10 rounded-full overflow-hidden" data-id="element-23124">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fnQWPJWMus62iBnJCYdU4W/z1.png" alt="Zoey" className="w-full h-full object-cover image-high-quality" loading="lazy" sizes="40px" data-id="element-23125" />
                </div>
                <div data-id="element-23126">
                  <div className="flex items-center gap-1" data-id="element-23127">
                    <h3 className="font-medium" data-id="element-23128">Zoey</h3>
                    <CheckCircle size={14} className="text-blue-400" fill="currentColor" data-id="element-23129" />
                    <span className="text-xs text-gray-400" data-id="element-23130">• {post.time}</span>
                  </div>
                  {post.location && <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-23131">
                      <MapPin size={10} className="ml-1" data-id="element-23132" />
                      <span data-id="element-23133">{post.location}</span>
                    </div>}
                </div>
                <button className="ml-auto" data-id="element-23134">
                  <MoreHorizontal size={16} className="text-gray-400" data-id="element-23135" />
                </button>
              </div>

              {/* Post Content */}
              <div className="px-3 py-2" data-id="element-23136">
                <p className="text-sm" data-id="element-23137">{post.content}</p>
                {/* Hashtags */}
                {post.hashtags && <div className="text-xs text-blue-400 mt-1" data-id="element-23138">
                    {post.hashtags.join(' ')}
                  </div>}
              </div>

              {/* Post Media */}
              {(post.type === 'image' || post.type === 'poll' && post.image) && post.image && <div className="aspect-square bg-gray-800" data-id="element-23139">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9yVK4ggPFZKtDWQ3fdeJ3s/z3.png" alt="Post" className="w-full h-full object-cover angel-post-image hover:scale-[1.02] transition-transform duration-300" loading="lazy" sizes="(max-width: 768px) 100vw, 820px" data-id="element-23140" />
                  </div>}

              {/* Premium Content */}
              {post.type === 'premium_content' && post.image && <div className="relative aspect-square bg-gray-800" data-id="element-23141">
                  <img src={post.image} alt="Locked Content" className="w-full h-full object-cover blur-sm angel-post-image" loading="lazy" sizes="(max-width: 768px) 100vw, 820px" data-id="element-23142" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-23143">
                    <Lock size={32} className="text-blue-400 mb-2" data-id="element-23144" />
                    <p className="text-center text-white font-medium" data-id="element-23145">
                      Unlock Exclusive Photo
                    </p>
                    <button className="mt-3 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full text-white font-medium flex items-center gap-2 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300" onClick={() => handleUnlock(post.id)} data-id="element-23146">
                      <span data-id="element-23147">Unlock for</span>
                      <span className="text-blue-200" data-id="element-23148">{post.price} 💎</span>
                    </button>
                  </div>
                </div>}

              {/* Poll */}
              {post.type === 'poll' && post.poll && <div className="p-3" data-id="element-23149">
                  <div className="space-y-2" data-id="element-23150">
                    {post.poll.options.map((option, i) => <button key={i} className={`w-full p-3 bg-gray-800 rounded-lg border ${pollVote === option.text ? 'border-blue-500' : 'border-gray-700 hover:border-blue-500'} transition flex justify-between items-center`} onClick={() => handlePollVote(option.text)} data-id="element-23151">
                        <span data-id="element-23152">{option.text}</span>
                        {(showPollResults || pollVote) && <span className="text-xs bg-gray-700 px-2 py-1 rounded-full" data-id="element-23153">
                            {option.percentage}%
                          </span>}
                      </button>)}
                    <p className="text-xs text-gray-400 mt-2" data-id="element-23154">
                      Voting ends in {post.poll.endsIn} • Results will shape
                      tonight's content
                    </p>
                  </div>
                </div>}

              {/* Voice Note */}
              {post.type === 'voice_note' && post.audioWaveform && <div className="p-3" data-id="element-23155">
                  <div className="p-3 bg-gray-800 rounded-lg border border-gray-700" data-id="element-23156">
                    <div className="flex items-center gap-3" data-id="element-23157">
                      <button className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300" data-id="element-23158">
                        <div className="ml-1" data-id="element-23159">▶</div>
                      </button>
                      <div className="flex-grow h-10 flex items-center" data-id="element-23160">
                        {post.audioWaveform.map((level, i) => <div key={i} className="w-1 mx-0.5 rounded-full bg-gradient-to-t from-blue-500/50 to-purple-500/50" style={{
                  height: `${level * 32}px`
                }} data-id="element-23161"></div>)}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-23162">
                        <span data-id="element-23163">{post.duration}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2" data-id="element-23164">
                      🎧 Best experienced with headphones
                    </p>
                  </div>
                </div>}

              {/* Post Actions */}
              <div className="p-3" data-id="element-23165">
                <div className="flex items-center justify-between" data-id="element-23166">
                  <div className="flex items-center gap-3" data-id="element-23167">
                    <button className="flex items-center gap-1 group" onClick={() => toggleLike(post.id)} data-id="element-23168">
                      <Heart size={18} className={likedPosts[post.id] ? 'text-blue-500' : 'text-gray-400 group-hover:text-blue-400'} fill={likedPosts[post.id] ? 'currentColor' : 'none'} data-id="element-23169" />
                      <span className="text-xs" data-id="element-23170">
                        {likedPosts[post.id] ? post.likes + 1 : post.likes}
                      </span>
                    </button>
                    <button className="flex items-center gap-1" data-id="element-23171">
                      <MessageCircle size={18} className="text-gray-400" data-id="element-23172" />
                      <span className="text-xs" data-id="element-23173">{post.comments}</span>
                    </button>
                    <button className="flex items-center gap-1" data-id="element-23174">
                      <Share2 size={18} className="text-gray-400" data-id="element-23175" />
                      <span className="text-xs" data-id="element-23176">Share</span>
                    </button>
                  </div>
                  <button className="flex items-center gap-1" data-id="element-23177">
                    <Gift size={18} className="text-purple-400" data-id="element-23178" />
                    <span className="text-xs" data-id="element-23179">Gift</span>
                  </button>
                </div>
                <div className="flex items-center gap-1 mt-2" data-id="element-23180">
                  <div className="flex -space-x-2" data-id="element-23181">
                    <div className="w-5 h-5 rounded-full border border-gray-900 bg-gray-700" data-id="element-23182"></div>
                    <div className="w-5 h-5 rounded-full border border-gray-900 bg-gray-700" data-id="element-23183"></div>
                  </div>
                  <span className="text-xs text-gray-400" data-id="element-23184">
                    Liked by David and {(post.likes - 1).toLocaleString()}{' '}
                    others
                  </span>
                </div>
                <div className="text-xs text-gray-400 mt-1" data-id="element-23185">
                  View all {post.comments} comments
                </div>
              </div>
            </div>) : <div className="bg-gray-900 rounded-xl border border-gray-800 p-8 text-center" data-id="element-23186">
            <div className="w-16 h-16 mx-auto flex items-center justify-center bg-gray-800 rounded-full mb-4" data-id="element-23187">
              {activeTab === 'media' ? <ImageIcon size={24} className="text-gray-600" data-id="element-23188" /> : activeTab === 'voice' ? <Mic size={24} className="text-gray-600" data-id="element-23189" /> : <Heart size={24} className="text-gray-600" data-id="element-23190" />}
            </div>
            <h3 className="text-lg font-bold mb-2" data-id="element-23191">
              No {activeTab} content yet
            </h3>
            <p className="text-gray-400 max-w-md mx-auto" data-id="element-23192">
              Zoey hasn't posted any {activeTab} content yet. Check back soon!
            </p>
          </div>}
      </div>

      {/* Mission Card */}
      <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl border border-blue-800/50 p-4 shadow-md hover:shadow-xl hover:shadow-blue-600/5 transition-all duration-300" data-id="element-23193">
        <div className="flex items-center gap-3" data-id="element-23194">
          <div className="w-10 h-10 flex items-center justify-center bg-blue-500/20 rounded-full text-blue-400" data-id="element-23195">
            🔥
          </div>
          <div data-id="element-23196">
            <h3 className="font-medium" data-id="element-23197">Daily Mission</h3>
            <p className="text-sm text-gray-300" data-id="element-23198">
              Comment on 3 of Zoey's posts
            </p>
          </div>
        </div>
        <div className="mt-3" data-id="element-23199">
          <div className="flex items-center justify-between mb-1" data-id="element-23200">
            <span className="text-xs text-gray-400" data-id="element-23201">Progress</span>
            <span className="text-xs text-gray-400" data-id="element-23202">
              {Object.values(likedPosts).filter(Boolean).length}/3
            </span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden" data-id="element-23203">
            <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" style={{
            width: `${Math.min(Object.values(likedPosts).filter(Boolean).length / 3 * 100, 100)}%`
          }} data-id="element-23204"></div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3" data-id="element-23205">
          <div className="flex items-center gap-1" data-id="element-23206">
            <span className="text-blue-400" data-id="element-23207">💙</span>
            <span className="text-sm" data-id="element-23208">+15</span>
          </div>
          <button className="text-xs px-3 py-1.5 bg-blue-500 rounded-full text-white font-medium hover:bg-blue-600 transition-colors" data-id="element-23209">
            View Posts
          </button>
        </div>
      </div>

      {/* Unlock Modal */}
      {showUnlockModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-23210">
          <div className="w-full max-w-sm bg-gray-900 rounded-xl border border-gray-800 overflow-hidden animate-in zoom-in-90 duration-300 shadow-xl" data-id="element-23211">
            <div className="p-4 border-b border-gray-800" data-id="element-23212">
              <h3 className="font-bold" data-id="element-23213">Unlock Premium Content</h3>
            </div>
            <div className="p-4" data-id="element-23214">
              <p className="text-sm text-gray-300 mb-4" data-id="element-23215">
                Unlock this exclusive content from Zoey for 5 gems. This is a
                one-time purchase.
              </p>
              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-23216">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-2xl" data-id="element-23217">
                  💎
                </div>
              </div>
              <div className="flex items-center justify-between mb-4" data-id="element-23218">
                <span className="text-sm" data-id="element-23219">Your balance:</span>
                <span className="font-bold" data-id="element-23220">15 💎</span>
              </div>
              <div className="flex items-center justify-between mb-4" data-id="element-23221">
                <span className="text-sm" data-id="element-23222">Cost:</span>
                <span className="font-bold text-blue-400" data-id="element-23223">-5 💎</span>
              </div>
              <div className="flex gap-2" data-id="element-23224">
                <button className="flex-1 py-2 bg-gray-800 rounded-lg text-sm hover:bg-gray-700 transition-colors" onClick={() => setShowUnlockModal(false)} data-id="element-23225">
                  Cancel
                </button>
                <button className="flex-1 py-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg text-white text-sm font-medium hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300" onClick={confirmUnlock} data-id="element-23226">
                  Unlock Now
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Image Generator Modal */}
      {showImageGeneratorModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-23227">
          <div className="relative w-full max-w-lg animate-in zoom-in-90 duration-300 bg-white rounded-lg shadow-lg" data-id="element-23228">
            <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100" onClick={() => setShowImageGeneratorModal(false)} data-id="element-23229">
              <X size={18} className="text-gray-500" data-id="element-23230" />
            </button>
            <div className="p-6" data-id="element-23231">
              <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-23232">
                Generate Image with Zoey
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-23233">
                Create a custom image with Zoey. Choose your options and
                generate a unique image.
              </p>
              <div className="space-y-4" data-id="element-23234">
                <div data-id="element-23235">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-23236">
                    Style
                  </label>
                  <input type="text" placeholder="Casual" className="w-full p-2 border border-gray-300 rounded-md text-gray-800" data-id="element-23237" />
                </div>
                <div data-id="element-23238">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-23239">
                    Mood
                  </label>
                  <input type="text" placeholder="Flirty" className="w-full p-2 border border-gray-300 rounded-md text-gray-800" data-id="element-23240" />
                </div>
                <div data-id="element-23241">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-23242">
                    Special Request
                  </label>
                  <textarea placeholder="Describe what you'd like to see..." className="w-full p-2 border border-gray-300 rounded-md text-gray-800 min-h-[100px]" data-id="element-23243"></textarea>
                </div>
                <div data-id="element-23244">
                  <div className="flex items-center justify-between mb-2" data-id="element-23245">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-23246">
                      Cost:
                    </label>
                    <span className="text-pink-500 font-medium flex items-center gap-1" data-id="element-23247">
                      15 <span className="text-blue-500" data-id="element-23248">💎</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-23249">
                    <button className="py-2 px-4 bg-gray-100 rounded-md text-gray-800 hover:bg-gray-200 transition-colors" onClick={() => setShowImageGeneratorModal(false)} data-id="element-23250">
                      Cancel
                    </button>
                    <button className="py-2 px-4 bg-pink-600 rounded-md text-white font-medium hover:bg-pink-700 transition-colors" data-id="element-23251">
                      Generate Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Video Generation Modal */}
      {showVideoUpsellModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-23252">
          <VideoGenerationUpsell onClose={() => setShowVideoUpsellModal(false)} onPurchase={(duration, tokens) => {
        // Handle purchase logic
        setShowVideoUpsellModal(false);
      }} data-id="element-23253" />
        </div>}

      <style jsx data-id="element-23254">{`
        .angel-feed-container {
          max-width: 860px;
          margin: 0 auto;
          padding: 0 16px;
        }
        @media (min-width: 1440px) {
          .angel-feed-container {
            max-width: 1080px;
          }
        }
        .angel-feed-post {
          max-width: 820px;
          margin: 0 auto;
        }
        .angel-post-image {
          width: 100%;
          height: auto;
          object-fit: cover;
          image-rendering: auto;
          border-radius: 0;
        }
        .image-high-quality {
          image-rendering: auto;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>;
};